import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Loader2, Newspaper } from 'lucide-react';
import { NEWS_FEED_PATH, newsCover, newsPagePath } from '../../constants/news.constants';
import { useNewsFeed, useNewsViewerId } from '../../hooks/useNewsReader';
import type { NewsCategory, NewsItem, NewsPlatform } from '../../types/news.types';
import { formatNewsDate, getApiErrorMessage } from '../../utils/news.utils';

const PAGER_BUTTON =
  'tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors';

/** Onglet du fil, dans l'URL (`?onglet=`) : un lien peut ouvrir directement « Mises à jour ». */
const UPDATES_TAB = 'mises-a-jour';

/**
 * Onglets du fil, admin et gestion SAV seulement : « Nouveautés » montre tout,
 * « Mises à jour » les seules mises à jour logicielles (`category` envoyé à l'API).
 */
const FEED_TABS: { key: string | null; label: string; category?: NewsCategory; emptyMessage: string }[] = [
  { key: null, label: 'Nouveautés', emptyMessage: 'Aucune nouveauté pour le moment.' },
  {
    key: UPDATES_TAB,
    label: 'Mises à jour',
    category: 'TECH',
    emptyMessage: 'Aucune mise à jour pour le moment.',
  },
];

function parsePage(raw: string | null): number {
  const page = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(page) && page >= 1 ? page : 1;
}

function FeedCard({ item }: { item: NewsItem }) {
  const publishedOn = formatNewsDate(item.publishedAt);
  const cover = newsCover(item);
  return (
    <li className="tw-list-none">
      <Link
        to={newsPagePath(item.slug)}
        className={`tw-group tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-p-4 tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-no-underline tw-transition-shadow hover:tw-shadow-md ${
          item.read ? 'tw-border-gray-200' : 'tw-border-affinicar-light'
        }`}
      >
        {cover.url && (
          <img
            src={cover.url}
            alt=""
            loading="lazy"
            className={`tw-w-full sm:tw-w-48 tw-h-40 sm:tw-h-28 tw-rounded-md tw-flex-shrink-0 ${
              cover.contain ? 'tw-object-contain tw-bg-white' : 'tw-object-cover tw-bg-gray-100'
            }`}
          />
        )}
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-1">
            {!item.read && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white tw-bg-affinicar-secondary tw-rounded tw-px-1.5 tw-py-0.5">
                <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-white" aria-hidden="true" />
                Non lue
              </span>
            )}
            {publishedOn && <span className="tw-text-xs tw-text-gray-500">{publishedOn}</span>}
          </div>
          <h2
            className={`tw-text-lg tw-mt-0 tw-mb-1 tw-break-words tw-text-gray-900 group-hover:tw-text-affinicar-secondary ${
              item.read ? 'tw-font-semibold' : 'tw-font-bold'
            }`}
          >
            {item.title}
          </h2>
          {item.shortDescription && (
            <p className="tw-text-sm tw-text-gray-600 tw-mb-2 tw-line-clamp-3 tw-whitespace-pre-line tw-break-words">
              {item.shortDescription}
            </p>
          )}
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-affinicar-secondary">
            Lire la suite
            <ArrowRight className="tw-w-4 tw-h-4" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </li>
  );
}

/**
 * Fil des nouveautés qui ciblent l'utilisateur sur la plateforme, dans l'ordre
 * fourni par l'API, paginé. La page courante est dans l'URL (`?page=`) : le
 * retour depuis une nouveauté retombe au même endroit.
 *
 * `showUpdatesTab` (admin et gestion SAV, où sont diffusées les mises à jour
 * logicielles) : onglets « Nouveautés » / « Mises à jour », eux aussi dans l'URL
 * (`?onglet=mises-a-jour`).
 */
export function NewsFeedPage({
  platform,
  showUpdatesTab = false,
}: {
  platform: NewsPlatform;
  showUpdatesTab?: boolean;
}) {
  const viewerId = useNewsViewerId(platform);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parsePage(searchParams.get('page'));
  const activeTab =
    (showUpdatesTab && FEED_TABS.find((tab) => tab.key === searchParams.get('onglet'))) || FEED_TABS[0];
  const { data, isError, error, isFetching, isPreviousData, refetch } = useNewsFeed(
    platform,
    page,
    activeTab.category,
  );

  const goToPage = (target: number) => {
    // On fusionne avec les paramètres existants plutôt que de les écraser.
    const next = new URLSearchParams(searchParams);
    if (target <= 1) next.delete('page');
    else next.set('page', String(target));
    setSearchParams(next);
  };

  // Changer d'onglet repart de la première page.
  const goToTab = (key: string | null) => {
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (key) next.set('onglet', key);
    else next.delete('onglet');
    setSearchParams(next);
  };

  const feedStartPath = activeTab.key ? `${NEWS_FEED_PATH}?onglet=${activeTab.key}` : NEWS_FEED_PATH;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.limit || 1))) : 1;
  const items = data?.items ?? [];

  return (
    <div className="tw-max-w-4xl tw-mx-auto tw-py-6">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2 tw-mb-6">
        {showUpdatesTab && viewerId ? (
          // Le titre sert d'interrupteur : l'onglet affiché en noir, l'autre en gris, cliquable.
          <h1 className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-5 tw-gap-y-1 tw-m-0">
            {FEED_TABS.map((tab) => {
              const selected = tab.key === activeTab.key;
              return (
                <button
                  key={tab.label}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => goToTab(tab.key)}
                  className={`tw-p-0 tw-bg-transparent tw-border-0 tw-text-2xl tw-font-bold tw-transition-colors ${
                    selected ? 'tw-text-gray-900 tw-cursor-default' : 'tw-text-gray-400 hover:tw-text-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </h1>
        ) : (
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">Nouveautés</h1>
        )}
        {data && data.unreadCount > 0 && (
          <span className="tw-text-sm tw-text-gray-600">
            {data.unreadCount > 1 ? `${data.unreadCount} non lues` : '1 non lue'}
          </span>
        )}
      </div>


      {!viewerId ? (
        <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
          Les nouveautés ne sont pas disponibles pour ce compte.
        </p>
      ) : isError && (!data || isPreviousData) ? (
        // `isPreviousData` : la page demandée a échoué et ce qui reste affichable est
        // la page d'avant — on ne la fait pas passer pour la page courante.
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-p-6 tw-text-center">
          <p className="tw-text-sm tw-text-red-700 tw-mb-4">
            {getApiErrorMessage(error, 'Impossible de charger les nouveautés pour le moment.')}
          </p>
          <button type="button" onClick={() => refetch()} className={PAGER_BUTTON}>
            Réessayer
          </button>
        </div>
      ) : !data ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[240px]" role="status" aria-label="Chargement des nouveautés">
          <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
        </div>
      ) : data.total === 0 ? (
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-10 tw-text-center">
          <Newspaper className="tw-w-10 tw-h-10 tw-text-gray-300 tw-mx-auto tw-mb-3" aria-hidden="true" />
          <p className="tw-text-sm tw-text-gray-600 tw-mb-0">{activeTab.emptyMessage}</p>
        </div>
      ) : items.length === 0 ? (
        // Page au-delà de la dernière (lien ancien, nouveauté retirée depuis).
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-10 tw-text-center">
          <p className="tw-text-sm tw-text-gray-600 tw-mb-4">Il n'y a rien sur cette page.</p>
          <Link
            to={feedStartPath}
            className="tw-text-sm tw-font-medium tw-text-affinicar-secondary hover:tw-text-affinicar-primary tw-underline"
          >
            Revenir au début du fil
          </Link>
        </div>
      ) : (
        <>
          <ul
            className={`tw-flex tw-flex-col tw-gap-4 tw-p-0 tw-m-0 tw-transition-opacity ${
              isPreviousData ? 'tw-opacity-60' : ''
            }`}
            aria-busy={isFetching}
          >
            {items.map((item) => (
              <FeedCard key={item.id} item={item} />
            ))}
          </ul>

          {totalPages > 1 && (
            <nav className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mt-6" aria-label="Pagination des nouveautés">
              <button type="button" className={PAGER_BUTTON} disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                <ChevronLeft className="tw-w-4 tw-h-4" aria-hidden="true" />
                Plus récentes
              </button>
              <span className="tw-text-sm tw-text-gray-600">
                Page {page} sur {totalPages}
              </span>
              <button
                type="button"
                className={PAGER_BUTTON}
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >
                Plus anciennes
                <ChevronRight className="tw-w-4 tw-h-4" aria-hidden="true" />
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
